import * as React from 'react';
import { useState, useEffect } from 'react';

/** Material UI Packages. */
import { Box, Typography } from '@mui/material';
import { randomId } from '@mui/x-data-grid-generator';

/** Redux Packages. */
import { useSelector, useDispatch } from 'react-redux';

/** Internal Components. */
import { createSocketData, deleteSocketData, readSocketData, handleProcessRowUpdate } from 'shared/lib/fetchSocket';
import { createFillDownHandler } from 'shared/lib/datagridHelpers';
import useInvalidOptionWarning from 'shared/hooks/useInvalidOptionWarning';
import SingleDatagrid from 'shared/ui/Datagrid';

import { notAllowNullKey, dateField } from 'data/shipmentDetailConfig';
import { aliasCol } from '../TableDefinition/OceanImportSubDef';

export default function UpdateSplit(props) {
    /** Get props from UpdateHBL.js */
    const { mainRow, containerRow, tabConfig } = props;

    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** State for original container alias data. */
    const containerAliasData = useSelector((state) => state.oimContainerAliasUpdateForm);

    /** Check if case_closure is Complete */
    const isCaseClosureComplete = mainRow?.case_closure === 'Complete';

    /** State for current table data. */
    const [aliasRows, setAliasRows] = useState([]);
    /** State to manage an autocomplete options useing websocket. */
    const [tradePartnerOptions, setTradePartnerOptions] = useState([]);
    /** State to manage an autocomplete options useing websocket(Volume Unit). */
    const [unitOptions, setUnitOptions] = useState([]);

    /**
     * Filter container alias data received from the server (stored in Redux) to include only the data related to the current shipment,
     * and update the state with these rows for the DataGrid.
     **/
    useEffect(() => {
        const filtered = containerAliasData.filter((item) => item.il_ocean_import_cntr_id == containerRow.id);
        setAliasRows(filtered);
    }, [containerRow, containerAliasData]);

    /** Handler for emitting to the server to add a new alias row. */
    const handleAddClick = () => {
        if (isCaseClosureComplete) return;
        const newAlias = { ...tabConfig.newAlias, id: randomId(), il_ocean_import_cntr_id: containerRow.id };
        createSocketData(dispatch, 0, 'row', 'il_ocean_import_cntr_alias', 'addOimAlias', newAlias);
    };

    /** Handle to delete a row from container alias table. */
    const handleDeleteClick = (id) => {
        if (isCaseClosureComplete) return;
        if (window.confirm('Are you sure you want to delete the entry?'))
            deleteSocketData(dispatch, 0, 'row', 'il_ocean_import_cntr_alias', 'deleteOimAlias', id);
    };

    /** Handle to change autocomplete trucker name and call Websocket to receive the searched result. */
    const handleTradePartnerChange = (event, value, params) => {
        const type = params.field;
        readSocketData(dispatch, 0, 'cell', 'il_trade_partner', type, value, setTradePartnerOptions);
    };

    /** Handle to change autocomplete opus unit list and call Websocket to receive the searched result. */
    const handleUnitChange = (event, value) => {
        readSocketData(dispatch, 0, 'cell', 'il_opus_volume_unit', '', value, setUnitOptions);
    };

    /** Prefetch unit/trucker options as soon as the cell enters edit mode, before the user types. */
    const handleCellEditStart = (params) => {
        if (params.field === 'il_opus_volume_unit_id') {
            handleUnitChange(null, params.value || '');
        } else if (params.field === 'il_trade_partner_id') {
            handleTradePartnerChange(null, params.value?.name ?? '', params);
        }
    };

    /** Warn the user when a pasted value is not one of the allowed select options. */
    const handleInvalidOption = useInvalidOptionWarning();

    /** Copy the first alias row's value in a column to every row below it. */
    const handleFillDown = createFillDownHandler({
        dispatch,
        disabled: isCaseClosureComplete,
        rows: aliasRows,
        tabValue: 0,
        methodTableName: 'il_ocean_import_cntr_alias',
        reducerName: 'setOimAliasFormData',
        getTableId: () => [mainRow.id, containerRow.il_ocean_import_hbl_id, containerRow.id],
        formData: containerAliasData,
        notAllowNullKey,
        dateField,
    });

    return (
        <>
            <Box sx={{ height: 'auto', py: 1, px: 2, border: '1px solid #f8f8f8', bgcolor: '#f1f1f1' }}>
                <Typography sx={{ fontSize: '11px', color: '#003a52', fontWeight: 500, mb: 0.3 }}>
                    Split Container Cargo
                </Typography>
                <SingleDatagrid
                    rows={aliasRows}
                    cols={aliasCol(
                        handleDeleteClick,
                        handleAddClick,
                        tradePartnerOptions,
                        handleTradePartnerChange,
                        unitOptions,
                        handleUnitChange,
                        mainRow,
                        containerRow,
                        handleFillDown,
                        handleInvalidOption
                    )}
                    processRowUpdate={(newRow) => {
                        if (isCaseClosureComplete)
                            return Promise.resolve(aliasRows.find((r) => r.id === newRow.id) || newRow);
                        return handleProcessRowUpdate({
                            dispatch,
                            tabValue: 0,
                            methodTableName: 'il_ocean_import_cntr_alias',
                            reducerName: 'setOimAliasFormData',
                            tableId: [mainRow.id, containerRow.il_ocean_import_hbl_id, containerRow.id],
                            formData: containerAliasData,
                            newRow,
                            notAllowNullKey,
                            dateField,
                        });
                    }}
                    autoHeight={true}
                    cellSelection={true}
                    sx={{
                        '& .MuiDataGrid-columnHeader': {
                            backgroundColor: '#dfdfdf',
                            textAlign: 'center',
                        },
                        '& .MuiDataGrid-scrollbarFiller': {
                            backgroundColor: '#dfdfdf',
                        },
                        '& .MuiDataGrid-row': {
                            backgroundColor: '#eeeeee',
                        },
                    }}
                    additional={{ onCellEditStart: handleCellEditStart }}
                />
            </Box>
        </>
    );
}
